/**
 * Author: Robert Banziziki Muhire
 * Date: 31/03/2020
 *
 */

import { combineLatest, from, Observable } from "rxjs";
import { ClientDeviceInfo, Cookies } from "./Device.interface";
import { screenTypeObservable } from "./display";
import { map, switchMap } from "rxjs/operators";
import { APP_CONFIG } from "../../config";
import { LOCALES, SupportedLanguages, DefaultLang } from "../../utils/i8n";
import { keys as Rkeys } from "ramda";
import { storageGet } from "./storage";
import { Device } from "@capacitor/device";

function importDevicePlugin() {
  return from(import("@capacitor/device")).pipe(map(({ Device }) => Device));
}

async function isIos() {
  const deviceInfo = await Device.getInfo();
  return APP_CONFIG.mode === "ios" || deviceInfo.platform === "ios";
}

async function isAndroid() {
  const { Device } = await import("@capacitor/device");
  const deviceInfo = await Device.getInfo();
  return APP_CONFIG.mode === "android" || deviceInfo.platform === "android";
}

async function isPwa() {
  const { Device } = await import("@capacitor/device");
  const deviceInfo = await Device.getInfo();
  if (APP_CONFIG.mode === "pwa") return true;

  if (APP_CONFIG.mode === "site") {
    return (
      window.matchMedia("(display-mode: standalone)").matches &&
      deviceInfo.platform === "web"
    );
  }
  return false;
}

async function isNative() {
  return (await isIos()) || (await isAndroid());
}

async function isInstalled() {
  return (await isNative()) || (await isPwa());
}

function getLanguageCode(): Observable<SupportedLanguages> {
  return importDevicePlugin().pipe(
    switchMap((device) => device.getLanguageCode()),
    map((lang) => {
      const deviceLanguage = lang.value.slice(0, 2).toUpperCase();
      if (Rkeys(LOCALES).includes(deviceLanguage)) {
        return deviceLanguage as SupportedLanguages;
      } else {
        return DefaultLang;
      }
    })
  );
}

function deviceInfoObservable() {
  const deviceInfo$ = importDevicePlugin().pipe(
    switchMap((device) => device.getInfo())
  );
  const screeType$ = screenTypeObservable();
  const languageCode$ = getLanguageCode();
  const isPwa$ = from(isPwa());

  return combineLatest([
    deviceInfo$,
    screeType$,
    languageCode$,
    isPwa$,
  ]).pipe(
    map((deviceData) => {
      const [deviceInfo, screen, languageCode, isStandAlone] =
        deviceData;
      const clientDeviceInfo: ClientDeviceInfo = {
        ...deviceInfo,
        notificationsEnabled: false,
      };
      clientDeviceInfo.standalone = isStandAlone;
      clientDeviceInfo.screen = screen;
      clientDeviceInfo.networkStatus = { connected: true };
      return { ...clientDeviceInfo, languageCode };
    })
  );
}

function loadCookiesObservable() {
  return from(storageGet("cookies")).pipe(
    map((cookies: Cookies) => {
      const installed = cookies?.installed ?? false;
      const cookieAgreement = cookies?.cookieAgreement ?? false;
      return { installed, cookieAgreement } as Cookies;
    })
  );
}

export {
  deviceInfoObservable,
  getLanguageCode,
  loadCookiesObservable,
  isAndroid,
  isIos,
  isInstalled,
  isNative,
};
